import { useState, useEffect } from "react";
import { Trash2, Pencil, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { Button, Modal, DatePicker } from "../../../components";
import axios from "axios";
import {
  GET_DELIVERY_SCHEDULES_URL,
  DELETE_DELIVERY_SCHEDULES_URL,
} from "../../../features/UrlHelper";
import DeliveryScheduleFormModal from "./DeliveryScheduleFormModal";
import "./DeliverySchedulesSection.scss";

const DAYS_OF_WEEK = [
  { id: 1, name: "Pondelok" },
  { id: 2, name: "Utorok" },
  { id: 3, name: "Streda" },
  { id: 4, name: "Štvrtok" },
  { id: 5, name: "Piatok" },
  { id: 6, name: "Sobota" },
  { id: 7, name: "Nedeľa" },
];

// Bezpečné parsovanie dátumu z API ako lokálny dátum (bez timezone posunu)
const parseLocalDate = (str) => {
  if (!str) return null;
  // ISO string s UTC timezone info (napr. "2026-08-06T22:00:00.000Z") –
  // split("T")[0] by dal UTC dátum "2026-08-06", čo je v GMT+2 nesprávne.
  // Preto vytvoríme Date a vyberieme lokálne časti (getFullYear/Month/Date).
  if (typeof str === "string" && str.length > 10) {
    const d = new Date(str);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  // Čistý dátumový reťazec "YYYY-MM-DD" – parsujeme priamo ako lokálny čas
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
};

// Formátovanie Date objektu na "D.M.YYYY"
const formatDate = (date) => {
  if (!date) return "-";
  return date.toLocaleDateString("sk-SK", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
};

// Skonvertuje Date na ISO string "YYYY-MM-DD"
const toISODate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Vráti dátum pondelka pre týždeň obsahujúci `date`
const getWeekMonday = (date) => {
  const d = new Date(date);
  const day = d.getDay(); // 0=Ned, 1=Pon ... 6=Sob
  const offset = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + offset);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Vráti pole 7 dátumov (Po–Ne) pre daný pondelok
const getWeekDays = (monday) => {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
};

// Skontroluje, či rozvrh pokrýva daný dátum
const scheduleCoversDate = (schedule, date) => {
  const from = parseLocalDate(schedule.valid_from);
  const to = schedule.valid_to ? parseLocalDate(schedule.valid_to) : null;
  if (!from) return false;
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  from.setHours(0, 0, 0, 0);
  if (d < from) return false;
  if (to) {
    to.setHours(0, 0, 0, 0);
    if (d > to) return false;
  }
  return true;
};

// Vráti JS deň týždňa (1=Pon ... 7=Ned) pre dátum
const getOurDayOfWeek = (date) => {
  const jsDay = date.getDay(); // 0=Ned
  return jsDay === 0 ? 7 : jsDay;
};

// Nájde aktívny deň pre konkrétny dátum s logikou priority výnimiek
const getActiveDayForDate = (date, schedules) => {
  const ourDay = getOurDayOfWeek(date);
  const validSchedules = schedules.filter((s) => scheduleCoversDate(s, date));

  // Výnimky (s valid_to) majú prioritu nad trvalými (valid_to === null)
  const exceptions = validSchedules.filter((s) => s.valid_to !== null);
  const permanent = validSchedules.filter((s) => s.valid_to === null);

  const source = exceptions.length > 0 ? exceptions[0] : permanent.length > 0 ? permanent[0] : null;
  if (!source) return null;

  const dayData = source.days.find((d) => d.day_of_week === ourDay);
  return dayData ? { ...dayData, isException: exceptions.length > 0 } : null;
};

// Vráti všetky rozvrhy, ktoré REÁLNE zasahujú do daného týždňa (Effective Dating).
// Trvalý rozvrh (valid_to = null) stráca platnosť dňom pred začiatkom novšieho trvalého rozvrhu.
const getSchedulesForWeek = (weekDays, schedules) => {
  const ws = new Date(weekDays[0]); ws.setHours(0, 0, 0, 0);
  const we = new Date(weekDays[6]); we.setHours(0, 0, 0, 0);

  // Trvalé rozvrhy (bez valid_to) – zoraď chronologicky
  const permanent = schedules
    .filter((s) => s.valid_to === null)
    .sort((a, b) => parseLocalDate(a.valid_from) - parseLocalDate(b.valid_from));

  // Výnimky (s explicitným valid_to)
  const exceptions = schedules.filter((s) => s.valid_to !== null);

  // Pre každý trvalý rozvrh vypočítaj efektívny koniec:
  // = deň pred začiatkom nasledujúceho trvalého rozvrhu (alebo nekonečno)
  const validPermanent = permanent.filter((s, i) => {
    const from = parseLocalDate(s.valid_from);
    from.setHours(0, 0, 0, 0);

    let effectiveTo = null;
    if (permanent[i + 1]) {
      effectiveTo = parseLocalDate(permanent[i + 1].valid_from);
      effectiveTo.setDate(effectiveTo.getDate() - 1);
      effectiveTo.setHours(0, 0, 0, 0);
    }

    if (from > we) return false;                          // Začína po konci týždňa
    if (effectiveTo && effectiveTo < ws) return false;    // Skončil pred začiatkom týždňa
    return true;
  });

  // Výnimky majú explicitný valid_to – originálna logika je správna
  const validExceptions = exceptions.filter((s) =>
    weekDays.some((d) => scheduleCoversDate(s, d))
  );

  return [...validPermanent, ...validExceptions];
};

function DeliverySchedulesSection() {
  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(true);
  const [weekStart, setWeekStart] = useState(() => getWeekMonday(new Date()));
  const viewFrom = toISODate(weekStart);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);


  useEffect(() => {
    loadSchedules();
  }, []);

  async function loadSchedules() {
    try {
      setLoadingSchedules(true);
      const response = await axios.get(GET_DELIVERY_SCHEDULES_URL);
      setSchedules(response.data);
    } catch (error) {
      console.error("Error fetching schedules:", error);
    } finally {
      setLoadingSchedules(false);
    }
  }

  const weekDays = getWeekDays(weekStart);
  const weekEnd = weekDays[6];
  const schedulesThisWeek = getSchedulesForWeek(weekDays, schedules);
  const todayIso = toISODate(new Date());

  const goToPrevWeek = () => {
    const prev = new Date(weekStart);
    prev.setDate(prev.getDate() - 7);
    setWeekStart(prev);
  };

  const goToNextWeek = () => {
    const next = new Date(weekStart);
    next.setDate(next.getDate() + 7);
    setWeekStart(next);
  };

  const handleViewFromChange = (isoStr) => {
    const d = new Date(isoStr);
    setWeekStart(getWeekMonday(d));
  };

  const handleViewToChange = (isoStr) => {
    const d = new Date(isoStr);
    // Posunutie na pondelok týždňa, v ktorom je koniec
    setWeekStart(getWeekMonday(d));
  };

  const getGroupKey = (group) => `${group.valid_from}|${group.valid_to || "null"}`;

  const getGroupLabel = (group) => {
    const from = formatDate(parseLocalDate(group.valid_from));
    if (!group.valid_to) return `od ${from}`;
    const to = formatDate(parseLocalDate(group.valid_to));
    return `${from} – ${to}`;
  };

  const isException = (group) => group.valid_to !== null;

  const openAddSchedule = () => {
    setEditingSchedule(null);
    setFormModalOpen(true);
  };

  const openEditSchedule = (group) => {
    setEditingSchedule(group);
    setFormModalOpen(true);
  };

  const deleteScheduleGroup = async (validFrom) => {
    try {
      await axios.delete(DELETE_DELIVERY_SCHEDULES_URL(validFrom));
      await loadSchedules();
    } catch (error) {
      console.error("Failed to delete schedule:", error);
    }
    setDeleteConfirm(null);
  };

  const renderTimeDisplay = (day) => {
    if (!day) return <span className="ds-time-cell__empty">–</span>;
    if (day.is_closed) {
      return <span className="ds-badge ds-badge--closed">Zatvorené</span>;
    }
    if (day.time_from && day.time_to) {
      return (
        <span className="ds-time-cell__time">
          {day.time_from.slice(0, 5)} – {day.time_to.slice(0, 5)}
        </span>
      );
    }
    return <span className="ds-time-cell__empty">–</span>;
  };

  if (loadingSchedules) {
    return <div className="admin__section">Načítavam...</div>;
  }

  return (
    <div className="admin__section ds-section">
      <div className="admin__sub-header">
        <h2 className="admin__section-title">Časy rozvozov</h2>
        <Button variant="primary" onClick={openAddSchedule}>
          <Plus size={16} /> Pridať nový rozvrh
        </Button>
      </div>

      {/* ── Navigácia týždňa ── */}
      <div className="ds__filter">
        <button className="ds__week-nav" onClick={goToPrevWeek} title="Predchádzajúci týždeň">
          <ChevronLeft size={18} />
        </button>
        <div className="ds__filter-pickers">
          <DatePicker label="Od" value={viewFrom} onChange={handleViewFromChange} />
          <span className="ds__filter-sep">–</span>
          <DatePicker label="Do" value={toISODate(weekEnd)} onChange={handleViewToChange} />
        </div>
        <button className="ds__week-nav" onClick={goToNextWeek} title="Nasledujúci týždeň">
          <ChevronRight size={18} />
        </button>
      </div>

      {/* ── Panel: Aktuálna otváracia doba pre zvolený týždeň ── */}
      <div className="ds-panel">
        <h3 className="ds-panel__title">Časy rozvozov pre zvolený týždeň</h3>
        {schedules.length === 0 ? (
          <div className="ds-empty">Žiadne rozvrhy. Začni kliknutím na "Pridať nový rozvrh".</div>
        ) : (
          <div className="ds-week-grid">
            {weekDays.map((date, idx) => {
              const activeDay = getActiveDayForDate(date, schedules);
              const dayName = DAYS_OF_WEEK[idx].name;
              const exception = activeDay?.isException;
              const isToday = toISODate(date) === todayIso;

              return (
                <div
                  key={date.toISOString()}
                  className={`ds-day-card${exception ? " ds-day-card--exception" : ""}${!activeDay ? " ds-day-card--empty" : ""}${isToday ? " ds-day-card--today" : ""}`}
                >
                  <div className="ds-day-card__header">
                    <span className="ds-day-card__name">{dayName}</span>
                    <span className="ds-day-card__date">{formatDate(date)}</span>
                  </div>
                  {exception && (
                    <span className="ds-badge ds-badge--exception">Výnimka</span>
                  )}
                  <div className="ds-day-card__time">
                    {renderTimeDisplay(activeDay)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Panel: Zoznam rozvrhov zasahujúcich do týždňa ── */}
      <div className="ds-panel">
        <h3 className="ds-panel__title">Rozvrhy platné v tomto týždni</h3>
        {schedulesThisWeek.length === 0 ? (
          <div className="ds-empty">Pre zvolený týždeň nie sú nastavené žiadne rozvrhy.</div>
        ) : (
          <div className="ds-schedule-list">
            {schedulesThisWeek.map((group) => {
              const key = getGroupKey(group);
              const exc = isException(group);

              return (
                <div key={key} className={`ds-schedule-item${exc ? " ds-schedule-item--exception" : ""}`}>
                  <div className="ds-schedule-item__header">
                    <div className="ds-schedule-item__meta">
                      <span className="ds-schedule-item__label">{getGroupLabel(group)}</span>
                      {exc && <span className="ds-badge ds-badge--exception">Výnimka</span>}
                    </div>
                    <div className="ds-schedule-item__actions">
                      <button
                        className="admin__icon-btn"
                        onClick={() => openEditSchedule(group)}
                        title="Upraviť"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        className="admin__icon-btn admin__icon-btn--danger"
                        onClick={() => setDeleteConfirm(group.valid_from)}
                        title="Vymazať"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="ds-schedule-item__days">
                    {group.days.map((day) => (
                      <div key={day.id} className="ds-schedule-day">
                        <span className="ds-schedule-day__name">
                          {DAYS_OF_WEEK.find((d) => d.id === day.day_of_week)?.name}
                        </span>
                        <span className="ds-schedule-day__time">{renderTimeDisplay(day)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Potvrdzovací modal vymazania ── */}
      <Modal isOpen={deleteConfirm !== null} onClose={() => setDeleteConfirm(null)}>
        <div className="modal__header">
          <h3>Potvrd vymazanie</h3>
        </div>
        <div className="modal__body">
          <p>Naozaj chceš vymazať tento rozvrh? Táto operácia sa nedá vrátiť.</p>
        </div>
        <div className="modal__footer">
          <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
            Zrušiť
          </Button>
          <Button variant="danger" onClick={() => deleteScheduleGroup(deleteConfirm)}>
            Vymazať
          </Button>
        </div>
      </Modal>

      {/* ── Formulárový modal ── */}
      <DeliveryScheduleFormModal
        isOpen={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        editingSchedule={editingSchedule}
        onSave={() => {
          loadSchedules();
          setFormModalOpen(false);
        }}
      />
    </div>
  );
}

export default DeliverySchedulesSection;
